import React from "react";
import { useNavigate } from "react-router-dom";
import MeterCatalogueController from "./MeterCatalogueController";
import {
  IDENTIFIER_SCHEMES,
  CONCEPT_STATUSES,
  AGGREGATION_METHODS,
  QUANTITIES,
  ELECTRICAL_CHARACTERISTICS,
  DIRECTIONS,
  PHASES,
  TIME_CHARACTERISTICS,
  READING_KINDS,
} from "./MeterCatalogueDTO";

/**
 * 🔌 METER DATA CATALOGUE (Site administration screen)
 *
 * The protocol-neutral taxonomy of utility-meter quantities SUSTINA ingests,
 * plus the register of physical meters. Each concept carries external
 * identifier mappings (OBIS today) and links to the ESG AttributeDefinitions it
 * feeds, so resolved readings flow into the declaration pipeline.
 *
 * Backed by ESG.MeterConcept* / ESG.MeterRegistration in RDB2
 * (.claude/db/create_meter_catalogue.sql). Same shape as KnowledgeBaseView.
 */
export default function MeterCatalogueView() {
  const navigate = useNavigate();
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const c = MeterCatalogueController(siteKey);

  const statusBadge = (s) => {
    const map = {
      verified: "bg-success",
      standard: "bg-primary",
      candidate: "bg-warning text-dark",
      draft: "bg-secondary",
      deprecated: "bg-dark",
    };
    return <span className={`badge ${map[s] || "bg-secondary"}`} style={{ fontSize: "0.6rem" }}>{s}</span>;
  };

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-2">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">Meter Data Catalogue</h2>
                <p className="text-muted small mb-0" style={{ maxWidth: 820 }}>
                  Protocol-neutral concepts for the utility-meter quantities SUSTINA ingests, their
                  external identifier mappings (OBIS, M-Bus, …) and the ESG attributes they feed.
                  Readings are resolved against this catalogue, then rolled up into declaration data.
                </p>
              </div>
            </div>

            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  className={`nav-link ${c.tab === "catalogue" ? "active" : ""}`}
                  onClick={() => c.setTab("catalogue")}
                >
                  Catalogue
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${c.tab === "meters" ? "active" : ""}`}
                  onClick={() => c.setTab("meters")}
                >
                  Meters
                </button>
              </li>
            </ul>

            {c.error && <div className="alert alert-danger extra-small">{c.error}</div>}
            {c.notice && <div className="alert alert-info extra-small">{c.notice}</div>}

            {c.tab === "catalogue" && (
              <div className="row g-3">
                {/* -------- concept list -------- */}
                <div className="col-lg-6">
                  <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto" }}
                      value={c.commodityFilter}
                      onChange={(e) => c.setCommodityFilter(e.target.value)}
                    >
                      <option value="">All commodities</option>
                      {c.ref.commodities.map((x) => (
                        <option key={x.code} value={x.code}>{x.label}</option>
                      ))}
                    </select>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto" }}
                      value={c.statusFilter}
                      onChange={(e) => c.setStatusFilter(e.target.value)}
                    >
                      <option value="">Any status</option>
                      {(c.ref.statuses.length ? c.ref.statuses : CONCEPT_STATUSES).map((x) => (
                        <option key={x.code} value={x.code}>{x.label}</option>
                      ))}
                    </select>
                    <input
                      className="form-control form-control-sm"
                      style={{ width: 160 }}
                      placeholder="search key / label"
                      value={c.search}
                      onChange={(e) => c.setSearch(e.target.value)}
                    />
                    <button className="btn btn-dark btn-sm ms-auto fw-bold" onClick={c.newConcept}>
                      + Concept
                    </button>
                  </div>

                  {c.loading ? (
                    <div className="text-muted small">
                      <span className="spinner-border spinner-border-sm me-2" />Loading…
                    </div>
                  ) : c.concepts.length === 0 ? (
                    <div className="alert alert-light border small">No concepts match.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm bg-white border small align-middle">
                        <thead className="table-dark">
                          <tr>
                            <th>Concept key</th>
                            <th>Unit</th>
                            <th>Status</th>
                            <th className="text-end">IDs</th>
                            <th className="text-end">Attr</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.concepts.map((x) => (
                            <tr key={x.meterConceptId}>
                              <td>
                                <button
                                  className="btn btn-link p-0 fw-bold text-dark text-decoration-none text-start"
                                  onClick={() => c.editConcept(x.meterConceptId)}
                                >
                                  {x.conceptKey}
                                </button>
                                <div className="extra-small text-muted">{x.label}</div>
                              </td>
                              <td>{x.canonicalUnitCode}</td>
                              <td>{statusBadge(x.statusCode)}</td>
                              <td className="text-end">{x.identifierCount}</td>
                              <td className="text-end">{x.attributeMapCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* -------- concept editor -------- */}
                <div className="col-lg-6">
                  {!c.draft ? (
                    <div className="alert alert-light border small">
                      Select a concept, or add one. External identifiers and ESG attribute links are
                      added after the concept is first saved.
                    </div>
                  ) : (
                    <div className="card border-0 shadow-sm bg-white p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">
                          {c.isNew ? "New concept" : c.draft.conceptKey}
                        </h6>
                        <button className="btn btn-sm btn-outline-secondary" onClick={c.cancelEdit}>Close</button>
                      </div>

                      <div className="mb-2">
                        <label className="extra-small fw-bold text-muted">Canonical key</label>
                        <input
                          className={`form-control form-control-sm ${c.conceptErrors.conceptKey ? "is-invalid" : ""}`}
                          value={c.draft.conceptKey}
                          disabled={!c.isNew}
                          onChange={(e) => c.setConceptField("conceptKey", e.target.value)}
                          placeholder="energy.active.import.total"
                        />
                        <div className="invalid-feedback">{c.conceptErrors.conceptKey}</div>
                      </div>

                      <div className="mb-2">
                        <label className="extra-small fw-bold text-muted">Label</label>
                        <input
                          className={`form-control form-control-sm ${c.conceptErrors.label ? "is-invalid" : ""}`}
                          value={c.draft.label}
                          onChange={(e) => c.setConceptField("label", e.target.value)}
                        />
                        <div className="invalid-feedback">{c.conceptErrors.label}</div>
                      </div>

                      <div className="row g-2">
                        <div className="col-6 mb-2">
                          <label className="extra-small fw-bold text-muted">Commodity</label>
                          <select
                            className="form-select form-select-sm"
                            value={c.draft.commodityCode}
                            onChange={(e) => c.setConceptField("commodityCode", e.target.value)}
                          >
                            {c.ref.commodities.map((x) => (
                              <option key={x.code} value={x.code}>{x.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6 mb-2">
                          <label className="extra-small fw-bold text-muted">Quantity</label>
                          <select
                            className="form-select form-select-sm"
                            value={c.draft.quantity}
                            onChange={(e) => c.setConceptField("quantity", e.target.value)}
                          >
                            {QUANTITIES.map((q) => <option key={q} value={q}>{q}</option>)}
                          </select>
                        </div>
                        <div className="col-6 mb-2">
                          <label className="extra-small fw-bold text-muted">Canonical unit</label>
                          <select
                            className={`form-select form-select-sm ${c.conceptErrors.canonicalUnitCode ? "is-invalid" : ""}`}
                            value={c.draft.canonicalUnitCode}
                            onChange={(e) => c.setConceptField("canonicalUnitCode", e.target.value)}
                          >
                            <option value="">—</option>
                            {c.ref.units.map((u) => (
                              <option key={u.code} value={u.code}>{u.code} — {u.label}</option>
                            ))}
                          </select>
                          <div className="invalid-feedback">{c.conceptErrors.canonicalUnitCode}</div>
                        </div>
                        <div className="col-6 mb-2">
                          <label className="extra-small fw-bold text-muted">Default aggregation</label>
                          <select
                            className="form-select form-select-sm"
                            value={c.draft.defaultAggregationMethodCode}
                            onChange={(e) => c.setConceptField("defaultAggregationMethodCode", e.target.value)}
                          >
                            {(c.ref.methods.length ? c.ref.methods : AGGREGATION_METHODS).map((m) => (
                              <option key={m.code} value={m.code}>{m.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-4 mb-2">
                          <label className="extra-small fw-bold text-muted">Elec. char.</label>
                          <select className="form-select form-select-sm" value={c.draft.electricalCharacteristic}
                            onChange={(e) => c.setConceptField("electricalCharacteristic", e.target.value)}>
                            {ELECTRICAL_CHARACTERISTICS.map((x) => <option key={x} value={x}>{x || "—"}</option>)}
                          </select>
                        </div>
                        <div className="col-4 mb-2">
                          <label className="extra-small fw-bold text-muted">Direction</label>
                          <select className="form-select form-select-sm" value={c.draft.direction}
                            onChange={(e) => c.setConceptField("direction", e.target.value)}>
                            {DIRECTIONS.map((x) => <option key={x} value={x}>{x || "—"}</option>)}
                          </select>
                        </div>
                        <div className="col-4 mb-2">
                          <label className="extra-small fw-bold text-muted">Phase</label>
                          <select className="form-select form-select-sm" value={c.draft.phase}
                            onChange={(e) => c.setConceptField("phase", e.target.value)}>
                            {PHASES.map((x) => <option key={x} value={x}>{x || "—"}</option>)}
                          </select>
                        </div>
                        <div className="col-6 mb-2">
                          <label className="extra-small fw-bold text-muted">Time characteristic</label>
                          <select className="form-select form-select-sm" value={c.draft.timeCharacteristic}
                            onChange={(e) => c.setConceptField("timeCharacteristic", e.target.value)}>
                            {TIME_CHARACTERISTICS.map((x) => <option key={x} value={x}>{x || "—"}</option>)}
                          </select>
                        </div>
                        <div className="col-3 mb-2">
                          <label className="extra-small fw-bold text-muted">Reading kind</label>
                          <select className="form-select form-select-sm" value={c.draft.readingKindHint}
                            onChange={(e) => c.setConceptField("readingKindHint", e.target.value)}>
                            {READING_KINDS.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                        </div>
                        <div className="col-3 mb-2">
                          <label className="extra-small fw-bold text-muted">Status</label>
                          <select className="form-select form-select-sm" value={c.draft.statusCode}
                            onChange={(e) => c.setConceptField("statusCode", e.target.value)}>
                            {(c.ref.statuses.length ? c.ref.statuses : CONCEPT_STATUSES).map((x) => (
                              <option key={x.code} value={x.code}>{x.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="mb-2">
                        <label className="extra-small fw-bold text-muted">Description</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={c.draft.description}
                          onChange={(e) => c.setConceptField("description", e.target.value)}
                          placeholder="Independently authored — no normative standard text."
                        />
                      </div>

                      {c.draft.statusCode === "deprecated" && (
                        <div className="mb-2">
                          <label className="extra-small fw-bold text-muted">Replaced by (MeterConceptId)</label>
                          <input
                            className={`form-control form-control-sm ${c.conceptErrors.replacedByMeterConceptId ? "is-invalid" : ""}`}
                            value={c.draft.replacedByMeterConceptId || ""}
                            onChange={(e) => c.setConceptField("replacedByMeterConceptId", e.target.value)}
                          />
                          <div className="invalid-feedback">{c.conceptErrors.replacedByMeterConceptId}</div>
                        </div>
                      )}

                      <div className="d-flex gap-2">
                        <button className="btn btn-dark btn-sm fw-bold" disabled={c.saving} onClick={c.saveConcept}>
                          {c.saving ? "Saving…" : c.isNew ? "Create concept" : "Save concept"}
                        </button>
                        {!c.isNew && (
                          <button className="btn btn-outline-danger btn-sm" onClick={() => c.retireConcept(c.draft.meterConceptId)}>
                            Retire
                          </button>
                        )}
                      </div>

                      {/* identifiers */}
                      {!c.isNew && (
                        <>
                          <hr />
                          <h6 className="extra-small fw-bold text-uppercase text-primary">Identifier mappings</h6>
                          {c.draft.identifiers.length === 0 && (
                            <div className="extra-small text-muted mb-2">None yet.</div>
                          )}
                          {c.draft.identifiers.map((i) => (
                            <div key={i.identifierId} className="d-flex align-items-center gap-2 mb-1 extra-small">
                              <span className="badge bg-light text-dark border">{i.schemeCode}</span>
                              <code className="text-dark">{i.displayCode}</code>
                              <span className="text-muted">{i.mappingContext}</span>
                              {statusBadge(i.statusCode)}
                              {i.mappingMethod === "AI_ASSISTED" && (
                                <span className="text-warning">AI {i.confidence ?? "?"}</span>
                              )}
                              <button
                                className="btn btn-link btn-sm p-0 text-danger ms-auto"
                                onClick={() => c.removeIdentifierRow(i.identifierId)}
                              >
                                remove
                              </button>
                            </div>
                          ))}
                          <div className="row g-2 mt-1">
                            <div className="col-3">
                              <select className="form-select form-select-sm" value={c.idForm.schemeCode}
                                onChange={(e) => c.setIdField("schemeCode", e.target.value)}>
                                {(c.ref.schemes.length ? c.ref.schemes : IDENTIFIER_SCHEMES).map((s) => (
                                  <option key={s.code} value={s.code}>{s.code}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-4">
                              <input
                                className={`form-control form-control-sm ${c.idErrors.displayCode ? "is-invalid" : ""}`}
                                placeholder="1-0:1.8.0"
                                value={c.idForm.displayCode}
                                onChange={(e) => c.setIdField("displayCode", e.target.value)}
                              />
                              <div className="invalid-feedback">{c.idErrors.displayCode}</div>
                            </div>
                            <div className="col-3">
                              <input
                                className="form-control form-control-sm"
                                placeholder="context"
                                value={c.idForm.mappingContext}
                                onChange={(e) => c.setIdField("mappingContext", e.target.value)}
                              />
                            </div>
                            <div className="col-2">
                              <button className="btn btn-outline-dark btn-sm w-100" disabled={c.saving} onClick={c.addIdentifierRow}>
                                add
                              </button>
                            </div>
                          </div>

                          {/* attribute links */}
                          <hr />
                          <h6 className="extra-small fw-bold text-uppercase text-primary">ESG attribute links</h6>
                          {c.draft.attributeMaps.length === 0 && (
                            <div className="extra-small text-muted mb-2">
                              Not linked to any AttributeDefinition yet.
                            </div>
                          )}
                          {c.draft.attributeMaps.map((m) => (
                            <div key={m.mapId} className="d-flex align-items-center gap-2 mb-1 extra-small">
                              <code className="text-dark">{m.attributeDefinitionId}</code>
                              {m.confidence != null && <span className="text-muted">conf {m.confidence}</span>}
                              <button
                                className="btn btn-link btn-sm p-0 text-danger ms-auto"
                                onClick={() => c.unlinkAttribute(m.mapId)}
                              >
                                unlink
                              </button>
                            </div>
                          ))}
                          <div className="row g-2 mt-1">
                            <div className="col-8">
                              <input
                                className="form-control form-control-sm"
                                placeholder="AttributeDefinitionId (GUID from Z_10)"
                                value={c.attrForm.attributeDefinitionId}
                                onChange={(e) => c.setAttrField("attributeDefinitionId", e.target.value)}
                              />
                            </div>
                            <div className="col-2">
                              <input
                                className="form-control form-control-sm"
                                placeholder="conf"
                                value={c.attrForm.confidence}
                                onChange={(e) => c.setAttrField("confidence", e.target.value)}
                              />
                            </div>
                            <div className="col-2">
                              <button className="btn btn-outline-dark btn-sm w-100" disabled={c.saving} onClick={c.linkAttribute}>
                                link
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {c.tab === "meters" && (
              <div className="row g-3">
                <div className="col-lg-7">
                  <div className="d-flex mb-2">
                    <button className="btn btn-dark btn-sm ms-auto fw-bold" onClick={c.newRegistration}>
                      + Meter
                    </button>
                  </div>
                  {c.regLoading ? (
                    <div className="text-muted small">
                      <span className="spinner-border spinner-border-sm me-2" />Loading…
                    </div>
                  ) : c.registrations.length === 0 ? (
                    <div className="alert alert-light border small">No meters registered.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm bg-white border small align-middle">
                        <thead className="table-dark">
                          <tr>
                            <th>Serial</th>
                            <th>Commodity</th>
                            <th>Site</th>
                            <th className="text-end">Readings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.registrations.map((r) => (
                            <tr key={r.meterRegistrationId}>
                              <td>
                                <button
                                  className="btn btn-link p-0 fw-bold text-dark text-decoration-none text-start"
                                  onClick={() => c.editRegistration(r)}
                                >
                                  {r.meterSerial}
                                </button>
                              </td>
                              <td>{r.commodityCode}</td>
                              <td>{r.siteReference || "—"}</td>
                              <td className="text-end">{r.readingCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="col-lg-5">
                  {!c.regDraft ? (
                    <div className="alert alert-light border small">
                      Select a meter, or add one. Binds a physical meter serial to a corporate entity
                      so its readings resolve to that entity for roll-up.
                    </div>
                  ) : (
                    <div className="card border-0 shadow-sm bg-white p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">
                          {c.regIsNew ? "New meter" : c.regDraft.meterSerial}
                        </h6>
                        <button className="btn btn-sm btn-outline-secondary" onClick={c.cancelRegEdit}>Close</button>
                      </div>
                      <div className="mb-2">
                        <label className="extra-small fw-bold text-muted">Meter serial</label>
                        <input
                          className={`form-control form-control-sm ${c.regErrors.meterSerial ? "is-invalid" : ""}`}
                          value={c.regDraft.meterSerial}
                          onChange={(e) => c.setRegField("meterSerial", e.target.value)}
                        />
                        <div className="invalid-feedback">{c.regErrors.meterSerial}</div>
                      </div>
                      <div className="mb-2">
                        <label className="extra-small fw-bold text-muted">Corporate entity id</label>
                        <input
                          className={`form-control form-control-sm ${c.regErrors.corporateEntityId ? "is-invalid" : ""}`}
                          value={c.regDraft.corporateEntityId}
                          onChange={(e) => c.setRegField("corporateEntityId", e.target.value)}
                          placeholder="GUID"
                        />
                        <div className="invalid-feedback">{c.regErrors.corporateEntityId}</div>
                      </div>
                      <div className="row g-2">
                        <div className="col-6 mb-2">
                          <label className="extra-small fw-bold text-muted">Commodity</label>
                          <select
                            className="form-select form-select-sm"
                            value={c.regDraft.commodityCode}
                            onChange={(e) => c.setRegField("commodityCode", e.target.value)}
                          >
                            {c.ref.commodities.map((x) => (
                              <option key={x.code} value={x.code}>{x.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6 mb-2">
                          <label className="extra-small fw-bold text-muted">Protocol</label>
                          <select
                            className="form-select form-select-sm"
                            value={c.regDraft.schemeCode}
                            onChange={(e) => c.setRegField("schemeCode", e.target.value)}
                          >
                            <option value="">—</option>
                            {(c.ref.schemes.length ? c.ref.schemes : IDENTIFIER_SCHEMES).map((s) => (
                              <option key={s.code} value={s.code}>{s.code}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6 mb-2">
                          <label className="extra-small fw-bold text-muted">Site reference</label>
                          <input
                            className="form-control form-control-sm"
                            value={c.regDraft.siteReference}
                            onChange={(e) => c.setRegField("siteReference", e.target.value)}
                          />
                        </div>
                        <div className="col-6 mb-2">
                          <label className="extra-small fw-bold text-muted">Meter time zone</label>
                          <input
                            className="form-control form-control-sm"
                            placeholder="Europe/Zurich"
                            value={c.regDraft.meterTimeZone}
                            onChange={(e) => c.setRegField("meterTimeZone", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button className="btn btn-dark btn-sm fw-bold" disabled={c.saving} onClick={c.saveRegistration}>
                          {c.saving ? "Saving…" : c.regIsNew ? "Register meter" : "Save meter"}
                        </button>
                        {!c.regIsNew && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => c.retireRegistration(c.regDraft.meterRegistrationId)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-5 pt-4 border-top">
              <button
                className="btn btn-link text-decoration-none p-0 text-secondary small"
                onClick={() => navigate("/registered/site-admin")}
              >
                <i className="bi bi-arrow-left me-2"></i> Return to Site Administration
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
